import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  getAllSongs,
  deleteSong,
  setLatestSong,
  getAllContactMessages,
  markMessageAsRead,
  deleteContactMessage,
  getAllTikTokVideos,
} from "../firebase/firebase-functions";
import AddSongForm from "../components/AddSongForm";
import TikTokManagement from "../components/TikTokManagement";
import BioManagement from "../components/BioManagement";
import HomeManagement from "../components/HomeManagement";
import "./dashboard.css";

const Dashboard = ({ homeConfig, onHomeConfigSaved, bio, onBioSaved }) => {
  const { user, userProfile, logout } = useAuth();
  const navigate = useNavigate();

  // Songs state
  const [songs, setSongs] = useState([]);
  const [songsLoading, setSongsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [songsError, setSongsError] = useState("");

  // Messages state
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messagesError, setMessagesError] = useState("");

  // TikTok Videos state
  const [tiktokVideos, setTiktokVideos] = useState([]);
  const [tiktokLoading, setTiktokLoading] = useState(true);

  useEffect(() => {
    fetchSongs();
    fetchMessages();
    fetchTikTokVideos();
  }, []);

  const fetchSongs = async () => {
    setSongsLoading(true);
    setSongsError("");
    try {
      const result = await getAllSongs();
      if (result.success) {
        setSongs(result.songs);
      } else {
        setSongsError(result.error || "Failed to load songs");
      }
    } catch (err) {
      setSongsError("Failed to load songs");
      console.error(err);
    } finally {
      setSongsLoading(false);
    }
  };

  const fetchMessages = async () => {
    setMessagesLoading(true);
    setMessagesError("");
    try {
      const result = await getAllContactMessages();
      if (result.success) {
        setMessages(result.messages);
      } else {
        setMessagesError(result.error || "Failed to load messages");
      }
    } catch (err) {
      setMessagesError("Failed to load messages");
      console.error(err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const fetchTikTokVideos = async () => {
    setTiktokLoading(true);
    try {
      const result = await getAllTikTokVideos();
      if (result.success) {
        setTiktokVideos(result.videos);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTiktokLoading(false);
    }
  };

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) navigate("/");
  };

  const handleDeleteSong = async (songId) => {
    if (!window.confirm("Are you sure you want to delete this song?")) return;
    const result = await deleteSong(songId);
    if (result.success) {
      fetchSongs();
    } else {
      alert("Failed to delete song: " + result.error);
    }
  };

  const handleSetLatest = async (songId) => {
    const result = await setLatestSong(songId);
    if (result.success) {
      fetchSongs();
    } else {
      alert("Failed to set latest song: " + result.error);
    }
  };

  const handleSongAdded = () => {
    fetchSongs();
    setShowAddForm(false);
  };

  const handleMarkAsRead = async (messageId) => {
    const result = await markMessageAsRead(messageId);
    if (result.success) {
      fetchMessages();
    } else {
      alert("Failed to mark message as read: " + result.error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?"))
      return;
    const result = await deleteContactMessage(messageId);
    if (result.success) {
      fetchMessages();
    } else {
      alert("Failed to delete message: " + result.error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown date";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const unreadCount = messages.filter((m) => m.status === "unread").length;

  return (
    <div className="dashboard-page">
      <section className="dashboard-hero-section">
        <div className="container">
          <div className="dashboard-hero-content">
            <h1 className="dashboard-title text-gradient">
              Anjali's Dashboard
            </h1>
            <p className="dashboard-subtitle">
              Welcome back, {userProfile?.displayName || user?.email}!
            </p>
          </div>
        </div>
      </section>

      <section className="dashboard-content-section">
        <div className="container">
          <div className="dashboard-actions">
            <button onClick={handleLogout} className="btn-secondary logout-btn">
              Logout
            </button>
          </div>

          {/* ── Contact Messages ─────────────────────────── */}
          <div className="messages-management-section">
            <div className="section-header">
              <h2>
                Contact Messages
                {unreadCount > 0 && (
                  <span className="unread-badge">{unreadCount} unread</span>
                )}
              </h2>
            </div>

            {messagesError && (
              <div className="error-banner">{messagesError}</div>
            )}

            {messagesLoading ? (
              <div className="loading-state">
                <div className="loader"></div>
                <p>Loading messages...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="empty-state">
                <h3>No messages yet</h3>
                <p>Messages from the contact form will appear here.</p>
              </div>
            ) : (
              <div className="messages-list">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`message-card ${
                      message.status === "unread" ? "unread" : ""
                    }`}
                  >
                    <div className="message-header">
                      <div className="message-sender">
                        <h3>{message.name || "Anonymous"}</h3>
                        {message.email && (
                          <a
                            href={`mailto:${message.email}`}
                            className="message-email"
                          >
                            {message.email}
                          </a>
                        )}
                      </div>
                      <div className="message-meta">
                        <span className="message-date">
                          {formatDate(message.timestamp)}
                        </span>
                        {message.status === "unread" && (
                          <span className="status-badge unread">Unread</span>
                        )}
                      </div>
                    </div>

                    <div className="message-body">
                      <p>{message.message}</p>
                    </div>

                    <div className="message-actions">
                      {message.status === "unread" && (
                        <button
                          onClick={() => handleMarkAsRead(message.id)}
                          className="btn-small btn-mark-read"
                        >
                          Mark as Read
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteMessage(message.id)}
                        className="btn-small btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Home Page Management ─────────────────────── */}
          <div className="home-management-section">
            <div className="section-header">
              <h2>🏠 Home Page</h2>
            </div>
            <HomeManagement
              homeConfig={homeConfig}
              onSaved={onHomeConfigSaved}
            />
          </div>

          {/* ── Bio Content ──────────────────────────────── */}
          <div className="bio-management-section">
            <div className="section-header">
              <h2>✍️ Bio Content</h2>
            </div>
            <BioManagement bio={bio} onSaved={onBioSaved} />
          </div>

          {/* ── Music Library ────────────────────────────── */}
          <div className="music-management-section">
            <div className="section-header">
              <h2>Music Library</h2>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="btn-primary"
              >
                {showAddForm ? "Cancel" : "Add New Song"}
              </button>
            </div>

            {songsError && <div className="error-banner">{songsError}</div>}

            {showAddForm && (
              <div className="add-song-container">
                <AddSongForm onSongAdded={handleSongAdded} />
              </div>
            )}

            {songsLoading ? (
              <div className="loading-state">
                <div className="loader"></div>
                <p>Loading songs...</p>
              </div>
            ) : songs.length === 0 ? (
              <div className="empty-state">
                <h3>No songs yet</h3>
                <p>Click "Add New Song" to upload your first track!</p>
              </div>
            ) : (
              <div className="songs-grid">
                {songs.map((song) => (
                  <div key={song.id} className="song-card">
                    <div className="song-cover">
                      <img src={song.coverUrl} alt={`${song.title} cover`} />
                      {song.isLatest && (
                        <div className="latest-badge">Latest</div>
                      )}
                    </div>

                    <div className="song-details">
                      <h3>{song.title}</h3>
                      <p className="song-artist">{song.artist}</p>

                      <div className="streaming-links-preview">
                        {song.streamingLinks?.spotify && (
                          <a
                            href={song.streamingLinks.spotify}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link-icon"
                            title="Spotify"
                          >
                            🎵
                          </a>
                        )}
                        {song.streamingLinks?.apple && (
                          <a
                            href={song.streamingLinks.apple}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link-icon"
                            title="Apple Music"
                          >
                            🍎
                          </a>
                        )}
                        {song.streamingLinks?.amazon && (
                          <a
                            href={song.streamingLinks.amazon}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link-icon"
                            title="Amazon Music"
                          >
                            📦
                          </a>
                        )}
                        {song.streamingLinks?.youtube && (
                          <a
                            href={song.streamingLinks.youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link-icon"
                            title="YouTube"
                          >
                            ▶️
                          </a>
                        )}
                      </div>

                      <div className="song-actions">
                        {!song.isLatest && (
                          <button
                            onClick={() => handleSetLatest(song.id)}
                            className="btn-small btn-latest"
                          >
                            Mark as Latest
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteSong(song.id)}
                          className="btn-small btn-delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── TikTok Videos ────────────────────────────── */}
          {tiktokLoading ? (
            <div className="loading-state">
              <div className="loader"></div>
              <p>Loading TikTok videos...</p>
            </div>
          ) : (
            <div className="tiktok-management-section">
              <TikTokManagement
                videos={tiktokVideos}
                onUpdate={fetchTikTokVideos}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
